/**
 * Pure view-model for the console's Requests surface.
 *
 * Everything the surface decides — which deliveries it is allowed to show, what
 * a row may say, how many are unread — lives here as pure functions, so it is
 * testable without a socket and without mounting the shell.
 *
 * The filter is the load-bearing part. `TeamInboxSync` is the org's whole
 * inbox: feedback requests, room messages, tracker comments, document
 * discussions and mentions all arrive on the same socket. They stay in this
 * one surface and are partitioned by chips rather than new navigation.
 */

import {
  isActivityRef,
  type TeamInboxMaterializedDelivery,
} from '@nimbalyst/collab-bundle/inbox';
import { getProjectRouteId, type ProjectSelectionCandidate } from '../projectSelection';
import { orgDocumentPath, type RouteTeam } from '../routing';
import type { CommentRef, InboxDelivery } from '@nimbalyst/collab-protocol';

export const REQUESTS_SOURCE_KIND = 'feedbackRequest';

export type RequestsFilterId = 'waiting' | 'mentions' | 'replies' | 'all';

export const REQUESTS_FILTERS: ReadonlyArray<{ id: RequestsFilterId; label: string }> = [
  { id: 'waiting', label: 'Waiting on me' },
  { id: 'mentions', label: 'Mentions' },
  { id: 'replies', label: 'Replies' },
  { id: 'all', label: 'All' },
];

export type RequestsSourceKind = CommentRef['sourceKind'];

const SOURCE_KIND_LABELS: Record<RequestsSourceKind, string> = {
  feedbackRequest: 'Feedback',
  documentDecision: 'Question',
  roomMessage: 'Room',
  dmMessage: 'Direct',
  trackerComment: 'Tracker',
  documentDiscussion: 'Document',
  documentInlineComment: 'Document',
};

const REASON_LABELS: Record<InboxDelivery['reason'], string> = {
  mention: 'Mention',
  agentMention: 'Agent mention',
  assignment: 'Assigned',
  reply: 'Reply',
  follow: 'Following',
  dm: 'Direct message',
};

export interface RequestRow {
  /** The inbox delivery. Mark-read and dismiss address this, not the request. */
  deliveryId: string;
  /** Present only when the row opens the `:requestId` feedback route. */
  requestId?: string;
  sourceId: string;
  sourceKind: RequestsSourceKind;
  documentTarget?: { projectId?: string; documentId: string; blockId?: string; threadId?: string; commentId?: string };
  reason?: InboxDelivery['reason'];
  orgId: string;
  /**
   * Who acted. The protocol `Actor` carries ids rather than names, so the id is
   * resolved against the org roster the console already loads. A delivery
   * whose actor is not on it gets no name rather than an invented one.
   */
  askedBy?: string;
  /** Bounded delivery preview; never a locally derived source body. */
  question?: string;
  kindLabel: string;
  activityLabel: string;
  /** The activity was created by an agent session on someone's behalf. */
  viaAgent: boolean;
  createdAt: number;
  unread: boolean;
}

export interface RequestRowGroup {
  id: string;
  label: string;
  rows: RequestRow[];
}

/**
 * A delivery addressed to an agent session rather than to a person.
 *
 * `FeedbackRequestRoom` wakes the author's session by delivering to the author's
 * own inbox, stamped with the same `feedbackRequest` resource kind as the rows
 * a recipient answers. The only thing separating the two is this targeting: a
 * wake carries the session ids it exists to reach, and a human-facing
 * assignment or nudge carries none. Without this check the author sees a second
 * row for their own request the moment it reaches quorum — the request they
 * asked, rendered back at them as though someone were waiting on them.
 */
function sourceIdentity(delivery: TeamInboxMaterializedDelivery): {
  sourceId: string;
  sourceKind: RequestsSourceKind;
} | null {
  const source = delivery.source;
  if (!source) return null;
  if (!isActivityRef(source)) {
    return { sourceId: source.sourceId, sourceKind: source.sourceKind };
  }
  const sourceKind = source.resourceKind === 'feedbackRequest'
    ? 'feedbackRequest'
    : source.resourceKind === 'tracker'
      ? 'trackerComment'
      : source.eventClass.startsWith('documentDecision')
        ? 'documentDecision'
        : 'documentDiscussion';
  return { sourceId: source.resourceId, sourceKind };
}

function isFeedbackAuthorWake(delivery: TeamInboxMaterializedDelivery): boolean {
  return (sourceIdentity(delivery)?.sourceKind === REQUESTS_SOURCE_KIND || sourceIdentity(delivery)?.sourceKind === 'documentDecision')
    && (delivery.agentSessionIds?.length ?? 0) > 0;
}

/**
 * A delivery this surface may render.
 *
 * An `unavailable` delivery carries no source at all — it is the shape the
 * server hydrates when the recipient may no longer read what it points at — so
 * it can never identify its source and is excluded rather than rendered as a
 * mystery row.
 */
export function isRequestsSurfaceDelivery(
  delivery: TeamInboxMaterializedDelivery,
): boolean {
  if (delivery.unavailable || !delivery.source) return false;
  return !isFeedbackAuthorWake(delivery);
}

/** Org member id to display name. Absent for an id the roster does not carry. */
export type ResolveActorName = (userId: string) => string | undefined;

export interface RequestRowOptions {
  resolveActorName?: ResolveActorName;
}

export function toRequestRow(
  delivery: TeamInboxMaterializedDelivery,
  { resolveActorName }: RequestRowOptions = {},
): RequestRow | null {
  if (!isRequestsSurfaceDelivery(delivery)) return null;
  const source = sourceIdentity(delivery);
  if (!source) return null;
  const actor = delivery.actor;
  // An agent asked on someone's behalf; the person is who the recipient knows.
  const authorId = actor?.kind === 'agent'
    ? actor.onBehalfOfUserId
    : actor?.userId ?? actor?.onBehalfOfUserId;
  return {
    deliveryId: delivery.id,
    ...(source.sourceKind === REQUESTS_SOURCE_KIND
      ? { requestId: source.sourceId }
      : {}),
    ...(delivery.source && (['documentInlineComment', 'documentDecision'].includes(source.sourceKind) || (isActivityRef(delivery.source) && delivery.source.resourceKind === 'document')) ? {
      documentTarget: {
        documentId: source.sourceId,
        projectId: delivery.source.projectId,
        blockId: delivery.source.blockId,
        threadId: delivery.source.threadId,
        commentId: delivery.source.commentId,
      },
    } : {}),
    sourceId: source.sourceId,
    sourceKind: source.sourceKind,
    reason: delivery.reason,
    orgId: delivery.orgId,
    askedBy: (authorId ? resolveActorName?.(authorId) : undefined)
      ?? delivery.preview?.actorLabel,
    question: (delivery.preview?.snippet ?? delivery.preview?.sourceTitle)?.replace(/\[([^\]]+)\]\(nimbalyst:\/\/[^)]+\)/g, '$1'),
    kindLabel: SOURCE_KIND_LABELS[source.sourceKind],
    activityLabel: delivery.reason
      ? REASON_LABELS[delivery.reason]
      : 'New activity',
    viaAgent: actor?.kind === 'agent',
    createdAt: delivery.createdAt,
    unread: !delivery.readAt || delivery.hasUnreadActivity,
  };
}

export interface SelectRequestRowsResult {
  /** Rows the list renders, newest first. */
  rows: RequestRow[];
  /** Unread rows across the whole surface, independent of the active filter. */
  unreadCount: number;
  /** Undismissed rows, independent of the active filter. */
  totalCount: number;
  /** Undismissed rows matching each chip, independent of the active filter. */
  filterCounts: Record<RequestsFilterId, number>;
}

function matchesRequestsFilter(row: RequestRow, filter: RequestsFilterId): boolean {
  switch (filter) {
    case 'waiting':
      return (row.sourceKind === REQUESTS_SOURCE_KIND || row.sourceKind === 'documentDecision' || row.reason === 'assignment') && row.unread;
    case 'mentions':
      return row.reason === 'mention' || row.reason === 'agentMention';
    case 'replies':
      return row.reason === 'reply';
    case 'all':
      return true;
  }
}

export function selectRequestRows(
  deliveries: readonly TeamInboxMaterializedDelivery[],
  filter: RequestsFilterId = 'all',
  options: RequestRowOptions = {},
): SelectRequestRowsResult {
  const all = deliveries
    .filter((delivery) => !delivery.dismissedAt)
    .map((delivery) => toRequestRow(delivery, options))
    .filter((row): row is RequestRow => row !== null)
    .sort((left, right) => right.createdAt - left.createdAt
      || right.deliveryId.localeCompare(left.deliveryId));

  const filterCounts = Object.fromEntries(
    REQUESTS_FILTERS.map(({ id }) => [
      id,
      all.filter((row) => matchesRequestsFilter(row, id)).length,
    ]),
  ) as Record<RequestsFilterId, number>;

  return {
    rows: all.filter((row) => matchesRequestsFilter(row, filter)),
    unreadCount: all.filter((row) => row.unread).length,
    totalCount: all.length,
    filterCounts,
  };
}

/** The gutter badge. Same admitted set as the list, so the two cannot disagree. */
export function countUnreadInboxRows(
  deliveries: readonly TeamInboxMaterializedDelivery[],
): number {
  return selectRequestRows(deliveries).unreadCount;
}

/** Explicit delivery ids "mark all read" acts on. */
export function unreadInboxDeliveryIds(
  deliveries: readonly TeamInboxMaterializedDelivery[],
): string[] {
  return selectRequestRows(deliveries).rows
    .filter((row) => row.unread)
    .map((row) => row.deliveryId);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Chronological buckets. `now` is a parameter so grouping is deterministic. */
export function groupRequestRows(rows: readonly RequestRow[], now: number): RequestRowGroup[] {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const todayMs = startOfToday.getTime();

  const buckets: RequestRowGroup[] = [
    { id: 'today', label: 'Today', rows: [] },
    { id: 'yesterday', label: 'Yesterday', rows: [] },
    { id: 'this-week', label: 'Earlier this week', rows: [] },
    { id: 'older', label: 'Older', rows: [] },
  ];

  for (const row of rows) {
    if (row.createdAt >= todayMs) buckets[0]!.rows.push(row);
    else if (row.createdAt >= todayMs - DAY_MS) buckets[1]!.rows.push(row);
    else if (row.createdAt >= todayMs - 7 * DAY_MS) buckets[2]!.rows.push(row);
    else buckets[3]!.rows.push(row);
  }

  return buckets.filter((bucket) => bucket.rows.length > 0);
}

/** Clock time inside today, weekday within the week, then a short date. */
export function formatRequestTimestamp(createdAt: number, now: number): string {
  const when = new Date(createdAt);
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  if (createdAt >= startOfToday.getTime()) {
    return when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  if (createdAt >= startOfToday.getTime() - 6 * DAY_MS) {
    return when.toLocaleDateString(undefined, { weekday: 'short' });
  }
  return when.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Old deliveries resolve only in a single visible project; never guess a room. */
/**
 * The project a document activity is read through, or null.
 *
 * A target that names its project wins; a single-project org is unambiguous;
 * anything else declines rather than route the document through a guessed
 * room, where it would resolve to nothing and look broken instead of absent.
 */
export function requestDocumentProject(team: RouteTeam, row: RequestRow): ProjectSelectionCandidate | null {
  const target = row.documentTarget;
  if (!target || row.orgId !== team.orgId) return null;
  const projects = team.projects ?? [];
  const project = target.projectId
    ? projects.find((candidate) => candidate.teamProjectId === target.projectId || candidate.projectId === target.projectId)
    : projects.length === 1 ? projects[0] : undefined;
  return project ?? null;
}

export function requestDocumentPath(team: RouteTeam, row: RequestRow): string | null {
  const project = requestDocumentProject(team, row);
  const target = row.documentTarget;
  return project && target ? orgDocumentPath(team, getProjectRouteId(project), target.documentId, target) : null;
}
